import React, { FC } from 'react';
import { Alert, Badge, UncontrolledTooltip } from 'reactstrap';
import {
  getReleasePeriod,
  PublicHotelReleaseGroup,
  ReleaseForceEntry,
  ReleaseGroupStage,
} from '../../../../shared/hotel';
import {
  CardListItemAction,
  CardListItemElement,
  formatDate,
  RichMarkdownIt,
} from '../../../components';
import { useTranslation } from '../../../translations';
import { displayName } from '../../../utils';
import { WaveEarlyEntryPeriodAlert } from '../util';

interface Props {
  readonly wave: PublicHotelReleaseGroup;
}

export const FCFSListItem: FC<Props> = ({ wave }) => {
  const now = new Date();
  const { ts } = useTranslation();

  const actions: CardListItemAction[] = [];
  const period = getReleasePeriod(wave, now);

  if (
    wave.forceEntry === ReleaseForceEntry.None &&
    (period === ReleaseGroupStage.EntryPeriod || period === ReleaseGroupStage.ReleasePeriod)
  ) {
    actions.push({
      text: 'hotel.wave.enter_btn',
      color: 'primary',
      id: 'enter-queue',
      to: `/hotel/wave/${wave.id}`,
    });
  } else {
    actions.push({
      text: 'hotel.wave.view_btn',
      color: 'secondary',
      id: 'view-wave',
      to: `/hotel/wave/${wave.id}`,
    });
  }

  return (
    <CardListItemElement
      actions={actions}
      badges={<GetBadge period={period} wave={wave} />}
      description={
        <>
          <RichMarkdownIt className="text-truncate d-block" source={wave.description} />
          <GetAlerts period={period} wave={wave} />
        </>
      }
      id={wave.id}
      subtitle={
        <ul className="list-unstyled">
          {wave.entryStartAt && (
            <li>
              <strong>{ts('early_entry_period')}</strong>: {formatDate(wave.entryStartAt)} &mdash;{' '}
              {formatDate(wave.entryEndAt!)}
            </li>
          )}
          <li>
            <strong>{ts('reservation_period')}</strong>: {formatDate(wave.releaseStartAt)} &mdash;{' '}
            {formatDate(wave.releaseEndAt)}
          </li>
        </ul>
      }
      title={displayName(wave)}
    />
  );
};

interface SubProps extends Props {
  readonly period: ReleaseGroupStage;
}

const GetBadge: FC<SubProps> = ({ wave, period }) => {
  const { ts } = useTranslation();
  const badges: React.JSX.Element[] = [];

  badges.push(
    <Badge color="secondary" key="random_draw">
      {ts('random_draw')}
    </Badge>,
  );

  switch (period) {
    case ReleaseGroupStage.PreEntry: {
      badges.push(
        <Badge color="warning" key="not_yet_open">
          {ts('not_yet_open')}
        </Badge>,
        <Badge color="info" key="opens">
          {ts('opens')}
          {formatDate(wave.releaseStartAt)}
        </Badge>,
      );

      break;
    }

    case ReleaseGroupStage.EntryPeriod: {
      badges.push(
        <Badge color="success" key="entries_accepted">
          {ts('entries_accepted')}
        </Badge>,
      );

      break;
    }

    case ReleaseGroupStage.PreReleaseReady: {
      badges.push(
        <React.Fragment key="preparing_for_release">
          <UncontrolledTooltip placement="bottom" target="preparingForRelease">
            {ts('the_reservation_period_has_not_yet_started')}
          </UncontrolledTooltip>
          <Badge color="warning" id="preparingForRelease">
            {ts('preparing_for_release')}
          </Badge>
        </React.Fragment>,
      );

      break;
    }

    case ReleaseGroupStage.ReleasePeriod: {
      badges.push(
        <Badge color="success" id="queueOpen" key="queue_open">
          {ts('queue_open')}
        </Badge>,
      );

      break;
    }

    case ReleaseGroupStage.Finished: {
      badges.push(
        <Badge color="secondary" key="finished">
          {ts('finished')}
        </Badge>,
      );

      break;
    }
  }

  return <>{badges}</>;
};

const GetAlerts: FC<SubProps> = ({ wave, period }) => {
  const { ts } = useTranslation();
  const alerts: React.JSX.Element[] = [];

  switch (period) {
    case ReleaseGroupStage.Finished: {
      alerts.push(
        <div key="finishedAlert">
          <hr />
          <p className="text-muted mt-2">{ts('hotel.release_has_finished')}</p>
        </div>,
      );

      break;
    }

    case ReleaseGroupStage.EntryPeriod: {
      alerts.push(<WaveEarlyEntryPeriodAlert key="earlyEntryAlert" wave={wave} />);
      // caution: break intentionally omitted -- EntryPeriod requires the additional alerts of ReleasePeriod
    }

    case ReleaseGroupStage.ReleasePeriod: {
      if (wave.eligibility.isEligible) {
        alerts.push(
          <Alert color="success" fade={false} id="hotel_eligible" key="eligible">
            {ts('you_are_eligible_to_enter_this_wave')}
          </Alert>,
        );
      } else {
        alerts.push(
          <Alert color="danger" fade={false} id="hotel_not_eligible" key="not_eligible">
            {ts('you_are_not_eligible_to_enter_this_wave')}
          </Alert>,
        );
      }

      break;
    }

    default: {
      break;
    }
  }

  return alerts;
};
